/*----------------------------------
- CATALOG: PUBLIC DOMAINS TABLE COLUMNS
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @docs docs/features/plain-words-vocabulary
 * @adr ADR-0028
 * @rule Ids, order, labels and provenance all come from the shared catalog. The public listing and
 *       the product table must show the same evidence: a visitor comparing the two should never
 *       find a column that exists only on one of them.
 *
 * The track widths left this catalog with the design-system rebuild (batch 7 part 3): they are the
 * table's `TRACKS` (`PublicDomainsTable.tsx`), the kit `Table`'s `colgroup` since batch 6 S2b, laid
 * out fixed at their sum.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import {
    COLUMN_PROVENANCE,
    PUBLIC_DOMAINS_TABLE_COLUMN_IDS,
    type ColumnProvenanceEntry,
} from '@uniquedomains/components/domains/catalogs/columnProvenance';

// Types
import type { NameRowBand, NameRowRatingBand } from '@uniquedomains/components/contracts/domains/nameRow';
import type { DomainsSearchTableVerdict } from '@uniquedomains/components/contracts/domains/search';

/*----------------------------------
- TYPES
----------------------------------*/

export type PublicDomainsTableColumn = {
    id: string;
    label: string;
    align: 'left' | 'right';
    provenance: ColumnProvenanceEntry;
};

/*----------------------------------
- CATALOG
----------------------------------*/

/** Labels and alignment, keyed by the shared column ids. Figures read to the right. */
const COLUMN_LAYOUT: Record<string, { label: string; align: 'left' | 'right' }> = {
    acquisition: { label: 'Status', align: 'left' },
    fingerprint: { label: 'Word profile', align: 'left' },
    timing: { label: 'Timing', align: 'left' },
    signals: { label: 'Signals', align: 'left' },
    verdict: { label: 'Rating', align: 'left' },
    askPrice: { label: 'Price', align: 'right' },
    upside: { label: 'Est. profit', align: 'right' },
    similarSales: { label: 'Similar sales', align: 'left' },
    comps: { label: 'TLD coverage', align: 'right' },
    liquidity: { label: 'Liquidity', align: 'right' },
    renewal: { label: 'Renews', align: 'right' },
};

/**
 * The public page's own provenance lines where the shared one promises what this page no longer shows.
 *
 * The website shows no checked time (the mock of 2026-10-25, "No checked time"), so the Timing head
 * cannot say "the hover shows the date checked": the public hover drops that date. The Product table
 * keeps the shared sentence, its hover still carries the date. Only the website catalog changes; the
 * Markdown mirror prints labels only, so its output is untouched.
 */
const PUBLIC_PROVENANCE_OVERRIDES: Record<string, Partial<ColumnProvenanceEntry>> = {
    timing: { refreshed: 'With each scan of the word.' },
};

export const PUBLIC_DOMAINS_TABLE_COLUMNS: readonly PublicDomainsTableColumn[] =
    PUBLIC_DOMAINS_TABLE_COLUMN_IDS.flatMap((id) => {
        const layout = COLUMN_LAYOUT[id];
        const provenance = COLUMN_PROVENANCE[id];
        return layout && provenance ? [{ id, ...layout, provenance: { ...provenance, ...PUBLIC_PROVENANCE_OVERRIDES[id] } }] : [];
    });

/**
 * The served rating's label and confidence as the kit `Rating`'s band and dots. A lookup of the
 * server's own words, never a grade computed here: the label is the band (product-design.md, bands
 * not scores), and the score the projection also carries is never printed on this page.
 */
export const PUBLIC_RATING_BANDS: Record<DomainsSearchTableVerdict['label'], NameRowRatingBand> = {
    'Strong choice': 'strong',
    Good: 'good',
    Risky: 'risky',
};
export const PUBLIC_RATING_CONFIDENCE: Record<DomainsSearchTableVerdict['confidence'], NameRowBand> = {
    High: 'high',
    Medium: 'medium',
    Low: 'low',
};

/**
 * The order label the public listing shows.
 *
 * The public order is the engine's daily shuffle, so "Relevance" is the only honest word for it.
 * The control beside it opens authentication rather than reordering (ADR-0028).
 */
export const PUBLIC_TABLE_SORT_LABEL = 'Relevance';
